import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2 } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link } from "react-router-dom";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import Swal from "sweetalert2";

const Index = () => {
  const [data, setData] = useState<any[]>([]);
  const [provinces, setProvinces] = useState<any[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [eventData, setEventData] = useState<any>(null);
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  }, []);

  // دریافت لیست شهرستان‌ها
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get("https://portal-api.mahanpanel.com/cities/", {
          params: sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder },
        });
        // پاسخ ممکن است مستقیم آرایه باشد یا داخل کلید items
        const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setData(items);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching cities:", error);
        setErrorMessage("خطا در دریافت لیست شهرستان‌ها");
      }
    };
    fetchData();
  }, [reload, sortBy, sortOrder]);

  // دریافت لیست استان‌ها برای انتخاب در فرم
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const res = await axios.get("https://portal-api.mahanpanel.com/provinces/");
        // پاسخ یک آرایه است
        const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setProvinces(items);
      } catch (error) {
        console.error("Error fetching provinces:", error);
      }
    };
    fetchProvinces();
  }, []);

  // حذف
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const onClickDelete = (row: any) => {
    setDeleteModal(true);
    setEventData(row);
  };

  const handleDelete = async () => {
    if (!eventData) return;
    try {
      const response = await axios.delete(
        `https://portal-api.mahanpanel.com/cities/${eventData.id}`
      );
      if (response.status === 200) {
        toast.success("شهرستان با موفقیت حذف شد");
        setData((prev) => prev.filter((item) => item.id !== eventData.id));
        setDeleteModal(false);
        setEventData(null);
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      toast.error("خطا در حذف شهرستان");
      setDeleteModal(false);
      setEventData(null);
    }
  };

  // فرم ثبت شهرستان
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      province_id: "",
      name: "",
    },
    validationSchema: Yup.object({
      province_id: Yup.string().required("لطفاً استان را انتخاب کنید"),
      name: Yup.string()
        .required("لطفاً نام شهرستان را وارد کنید")
        .min(2, "حداقل ۲ کاراکتر"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          name: values.name,
          province_id: parseInt(values.province_id, 10),
        };
        const response = await axios.post(
          "https://portal-api.mahanpanel.com/cities/",
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        if (response.status === 201) {
          // toast.success();
          Swal.fire("عملیات موفق", `شهرستان "${values.name}" با موفقیت ثبت شد`, "success")
          setReload(!reload);
          toggle();
        }
      } catch (error: any) {
        console.error("Create error:", error);
        if (error.response?.status === 422) {
          toast.error("اطلاعات وارد شده نامعتبر است");
        } else {
          toast.error("خطا در ثبت شهرستان");
        }
      }
    },
  });

  const toggle = useCallback(() => {
    setShow((prev) => !prev);
    if (!show) {
      validation.resetForm();
      setEventData(null);
    }
  }, [show, validation]);

  const handleSubmitButton = (e: React.MouseEvent) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "id",
        cell: (cell: any) => <span>{cell.getValue()}</span>,
      },
      {
        header: "نام شهرستان",
        accessorKey: "name",
        cell: (cell: any) => (
          <Link
            to="#!"
            className="transition-all duration-150 ease-linear text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "استان",
        accessorKey: "province_name",
        cell: (cell: any) => <span>{cell.getValue() || "-"}</span>,
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <div className="flex gap-3">
            <Link to="#!" onClick={() => onClickDelete(cell.row.original)}>
              <Trash2 className="size-4 text-red-500 hover:text-red-700" />
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="ثبت شهرستان" title="ماهان پانل" />
      <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
      <ToastContainer position="top-center" closeButton={false} limit={1} rtl className="font-bold text-lg" />
      {errorMessage && <div className="text-center text-red-500 mb-3">{errorMessage}</div>}
      <div className="card" id="employeeTable rtl">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع شهرستان‌ها (<b className="total-Employs">{data.length}</b>)
            </h6>
            <div className="shrink-0">
              <button
                onClick={toggle}
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
              >
                <span className="align-middle rtl">ثبت شهرستان جدید</span>{" "}
                <Plus className="inline-block size-4 rtl" />
              </button>
            </div>
          </div>
          {data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={5}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter
              count={errorMessage ? 1 : 15}
              text={errorMessage || "داده‌ای در جدول وجود ندارد"}
            />
          )}
        </div>
      </div>

      {/* مودال ثبت شهرستان */}
      <Modal show={show} onHide={toggle} modal-center="true" dialogClassName="w-screen md:w-[30rem]">
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">اضافه کردن شهرستان</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={validation.handleSubmit} id="create-form">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <div className="xl:col-span-11 dark:text-white text-gray-800">
                <div className="flex items-center">
                  <label htmlFor="province_id" className="text-base font-medium mr-2 w-1/4">
                    استان:
                  </label>
                  <select
                    id="province_id"
                    name="province_id"
                    className="form-select font-[12px] flex-1"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.province_id}
                  >
                    <option value="">انتخاب استان</option>
                    {provinces.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                {validation.touched.province_id && validation.errors.province_id && (
                  <p className="text-red-400 mr-24 mt-2">
                    {typeof validation.errors.province_id === "string"
                      ? validation.errors.province_id
                      : ""}
                  </p>
                )}
              </div>
              <div className="xl:col-span-11 dark:text-white text-gray-800">
                <div className="flex items-center">
                  <label htmlFor="name" className="text-base font-medium mr-2 w-1/4">
                    نام شهرستان:
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input font-[12px] flex-1"
                    placeholder="ثبت شهرستان"
                    autoComplete="off"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name}
                  />
                </div>
                {validation.touched.name && validation.errors.name && (
                  <p className="text-red-400 mr-24 mt-2">
                    {typeof validation.errors.name === "string"
                      ? validation.errors.name
                      : ""}
                  </p>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleSubmitButton}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:ring focus:ring-custom-100 dark:focus:ring-custom-400/20"
          >
            ثبت
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
